import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { organizationService } from '../services/organizationService';
import { toast } from 'react-hot-toast';

interface UseOrganizationSwitchReturn {
  switchOrganization: (organizationId: string) => Promise<void>;
  isSwitching: boolean;
  switchingOrgId: string | null;
}

export const useOrganizationSwitch = (): UseOrganizationSwitchReturn => {
  const { switchOrganization: contextSwitchOrganization, refreshUserData } = useAuth();
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);

  const switchOrganization = useCallback(async (organizationId: string): Promise<void> => {
    setIsSwitching(true);
    setSwitchingOrgId(organizationId);
    
    try {
      // Call the API to switch organization
      await organizationService.switchOrganization(organizationId);
      
      // Update the context state
      await contextSwitchOrganization(organizationId);
      
      // Refresh user data to get updated organization context
      await refreshUserData();
      
      toast.success('Organization switched successfully!', {
        icon: '🎯',
        duration: 2000,
      });
    } catch (_error) {
      // console.error('Organization switch error:', _error);
      const errorMessage = _error instanceof Error ? _error.message : 'Failed to switch organization';
      toast.error(errorMessage);
      throw _error;
    } finally {
      setIsSwitching(false);
      setSwitchingOrgId(null);
    }
  }, [contextSwitchOrganization, refreshUserData]);

  return {
    switchOrganization,
    isSwitching,
    switchingOrgId
  };
};
